/**
 * DraftBlock — egy dolgozói tervezet pill a beosztási rácsban.
 *
 * Két soros, ShiftBlock-méretű (~32px). Az egér ráhúzásakor a teljes
 * pillen működő tooltip mutatja a részleteket (műszak neve, idősáv,
 * óraszám, opcionális megjegyzés). A ✓ / × gombok továbbra is külön
 * működnek (stopPropagation).
 */
import { Check, X, Loader2, Clock, Moon, FileText } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { colorStyles, fmtHours } from "@/pages/schedule/helpers";

export default function DraftBlock({ draft, onApprove, onReject, loading }) {
  const cs = colorStyles(draft.color || (draft.is_day_off ? "slate" : "indigo"));
  const isDayOff = !!draft.is_day_off;
  const id = draft.id || draft._id;
  const name = isDayOff ? "Szabadnap" : (draft.template_name || "Tervezet");
  const timeLabel = isDayOff ? "egész nap" : `${draft.start_time}–${draft.end_time}`;

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            data-testid={`draft-block-${id}`}
            className={cn(
              "group/dr relative w-full overflow-hidden rounded-md border border-dashed bg-white/40 transition-all hover:-translate-y-[0.5px] hover:bg-white/70 hover:shadow-[0_4px_10px_-4px_rgba(15,23,42,0.18)] dark:bg-card/30 dark:hover:bg-card/50",
              cs.block.replace(/bg-\S+/g, ""), // border + text colors only
            )}
          >
            {/* Left accent stripe */}
            <span
              className={cn("absolute inset-y-0 left-0 w-[3px] rounded-l-md", cs.dot)}
              aria-hidden
            />

            <div className="py-1 pl-3 pr-[40px]">
              <div className="flex items-center gap-1.5">
                <span className="shrink-0 rounded-[3px] bg-foreground/[0.08] px-[3px] py-[0.5px] text-[8.5px] font-bold uppercase leading-none tracking-[0.1em]">
                  T
                </span>
                <p className="truncate text-[11.5px] font-semibold leading-tight tracking-tight">
                  {name}
                </p>
                {!isDayOff && (
                  <span className="ml-auto shrink-0 font-mono text-[9.5px] font-medium tabular-nums opacity-70">
                    {fmtHours(draft.duration_hours)}h
                  </span>
                )}
              </div>
              <p className="mt-0.5 truncate font-mono text-[10px] font-medium uppercase tracking-wider tabular-nums opacity-65">
                {timeLabel}
              </p>
            </div>

            {/* Action buttons — absolute, no row growth */}
            <div className="absolute right-1 top-1/2 -translate-y-1/2 flex flex-col items-center gap-0.5">
              <button
                type="button"
                data-testid={`draft-block-approve-${id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!loading) onApprove?.(draft);
                }}
                onPointerDown={(e) => e.stopPropagation()}
                disabled={loading}
                aria-label="Jóváhagy"
                className="grid h-[14px] w-[14px] place-items-center rounded-full bg-emerald-500 text-white shadow-[0_1px_3px_-1px_rgba(16,185,129,0.6)] transition-all hover:bg-emerald-600 active:scale-90 disabled:opacity-50"
              >
                {loading ? (
                  <Loader2 className="h-2 w-2 animate-spin" strokeWidth={3} />
                ) : (
                  <Check className="h-[9px] w-[9px]" strokeWidth={4} />
                )}
              </button>
              <button
                type="button"
                data-testid={`draft-block-reject-${id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!loading) onReject?.(draft);
                }}
                onPointerDown={(e) => e.stopPropagation()}
                disabled={loading}
                aria-label="Elutasít"
                className="grid h-[14px] w-[14px] place-items-center rounded-full bg-white/90 text-rose-600 ring-1 ring-rose-200 transition-all hover:bg-rose-50 active:scale-90 disabled:opacity-50 dark:bg-card/90 dark:ring-rose-900/40"
              >
                <X className="h-[9px] w-[9px]" strokeWidth={3} />
              </button>
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          align="start"
          className="border-slate-200 bg-white p-0 text-foreground shadow-[0_12px_30px_-12px_rgba(15,23,42,0.35)] dark:border-border dark:bg-card"
        >
          <div className="min-w-[200px] divide-y divide-slate-100 dark:divide-border/60">
            {/* Header */}
            <div className="flex items-center gap-2 px-3 py-2">
              <span className={cn("grid h-7 w-7 shrink-0 place-items-center rounded-lg", cs.block)}>
                {isDayOff ? (
                  <Moon className="h-3.5 w-3.5" strokeWidth={2} />
                ) : (
                  <span className={cn("h-3 w-3 rounded-full", cs.dot)} />
                )}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[9.5px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                  Tervezet
                </p>
                <p className="truncate text-[13px] font-semibold tracking-tight">
                  {name}
                </p>
              </div>
            </div>

            {/* Time + duration */}
            <div className="flex items-center gap-2 px-3 py-2">
              <div className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-slate-50 text-slate-600 dark:bg-foreground/[0.05]">
                <Clock className="h-3.5 w-3.5" strokeWidth={2} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[9.5px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                  {isDayOff ? "Időtartam" : "Mettől – meddig"}
                </p>
                <p className="font-mono text-[13px] font-semibold tabular-nums">
                  {timeLabel}
                  {!isDayOff && (
                    <span className="ml-1.5 text-[10.5px] font-medium text-slate-500">
                      · {fmtHours(draft.duration_hours)}h
                    </span>
                  )}
                </p>
              </div>
            </div>

            {draft.notes && (
              <div className="flex items-start gap-2 px-3 py-2">
                <div className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-amber-50 text-amber-700 dark:bg-amber-500/15">
                  <FileText className="h-3.5 w-3.5" strokeWidth={2} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[9.5px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                    Megjegyzés
                  </p>
                  <p className="text-[12px] italic leading-snug text-slate-700 dark:text-foreground">
                    {`„${draft.notes}”`}
                  </p>
                </div>
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
